import type { AuthContextValue, AuthUser } from './types'
import { createShellBffClient, ShellBffHttpError } from '@offer/bff-client-core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { consumePostAuthRedirect, isInternalPath, redirectToAuthLogin } from './authRedirect'

interface UseAuthOptions {
  bffBaseUrl?: string
}

interface MeResponse {
  authenticated: boolean
}

interface UserInfoResponse {
  id?: string
  email?: string
  name?: string
  givenName?: string
  surname?: string
  job?: string
  roles?: string[]
}

export function useAuth(options: UseAuthOptions = {}): AuthContextValue {
  const bffBaseUrl = options.bffBaseUrl ?? ''
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const hasCode = Boolean(code)
  const handledExchangeCodeRef = useRef<string | null>(null)

  const client = useMemo(
    () => createShellBffClient({
      baseUrl: bffBaseUrl || undefined,
      resolvePath: (_, path) => path,
    }),
    [bffBaseUrl],
  )

  const meQuery = useQuery({
    queryKey: ['auth-me'],
    enabled: !hasCode,
    retry: false,
    queryFn: (): Promise<MeResponse> =>
      client.request<MeResponse>({ child: '', path: '/auth/me' }),
  })

  const exchangeQuery = useQuery({
    queryKey: ['auth-exchange', code],
    enabled: Boolean(hasCode),
    retry: false,
    queryFn: async () => {
      try {
        const { csrfToken } = await client.request<{ csrfToken: string }>({ child: '', path: '/auth/csrf' })
        await client.request({
          child: '',
          path: '/auth/exchange',
          method: 'POST',
          body: { code, ...(state ? { state } : {}) },
          headers: { 'x-csrf-token': csrfToken },
        })
      }
      catch (error) {
        if (error instanceof ShellBffHttpError) {
          const details = (error.responseBody as { message?: string })?.message ?? ''

          throw new Error(details ? `Code exchange failed: ${details}` : 'Code exchange failed')
        }
        throw error
      }

      return true
    },
  })

  const isExchangeSuccess = hasCode && (exchangeQuery.isSuccess)
  const isSessionAuthenticated = !hasCode && meQuery.data?.authenticated === true
  const isAuthenticated = isExchangeSuccess || isSessionAuthenticated

  const userInfoQuery = useQuery({
    queryKey: ['auth-userinfo'],
    enabled: isAuthenticated,
    retry: false,
    queryFn: (): Promise<UserInfoResponse> =>
      client.request<UserInfoResponse>({ child: '', path: '/auth/userinfo' }),
  })

  const userData = userInfoQuery.data
  const userId = userData?.id
  const userEmail = userData?.email
  const userFullName = userData?.name
  const userGivenName = userData?.givenName
  const userSurname = userData?.surname
  const userJob = userData?.job
  const userRoles = userData?.roles

  const user = useMemo<AuthUser | null>(
    () =>
      userId && userEmail && userFullName
        ? {
            id: userId,
            email: userEmail,
            name: userFullName,
            roles: userRoles ?? [],
            givenName: userGivenName,
            surname: userSurname,
            job: userJob,
          }
        : null,
    [userId, userEmail, userFullName, userRoles, userGivenName, userSurname, userJob],
  )

  const isLoading = hasCode
    ? exchangeQuery.isPending
    : meQuery.isFetching || (isAuthenticated && userInfoQuery.isFetching)

  const completeCodeExchangeRedirect = useCallback(() => {
    const returnPath = consumePostAuthRedirect()
    if (returnPath && isInternalPath(returnPath)) {
      navigate(returnPath, { replace: true })
    }
    else {
      navigate('/', { replace: true })
    }
  }, [navigate])

  const redirectToLogin = useCallback(() => redirectToAuthLogin('/bff/auth/login'), [])

  const restorePostAuthRedirect = useCallback(() => {
    const returnPath = consumePostAuthRedirect()
    if (returnPath && isInternalPath(returnPath) && returnPath !== currentPath) {
      navigate(returnPath, { replace: true })
    }
  }, [currentPath, navigate])

  useEffect(() => {
    if (hasCode) {
      if (!exchangeQuery.isSuccess || handledExchangeCodeRef.current === code) {
        return
      }

      handledExchangeCodeRef.current = code
      completeCodeExchangeRedirect()
      return
    }

    handledExchangeCodeRef.current = null
    const sessionAuthenticated = meQuery.data?.authenticated
    if (sessionAuthenticated === undefined) {
      return
    }

    if (sessionAuthenticated) {
      restorePostAuthRedirect()
    }
    else {
      redirectToLogin()
    }
  }, [
    hasCode,
    code,
    exchangeQuery.isSuccess,
    meQuery.data?.authenticated,
    completeCodeExchangeRedirect,
    redirectToLogin,
    restorePostAuthRedirect,
  ])

  return useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
    }),
    [user, isAuthenticated, isLoading],
  )
}
